import _ from 'lodash';
import { DiagnosisResponse, ChartData, ChartSeries } from '../../types';

/**
 * Transforms diagnosis statistics into appointment counts chart data
 * @param diagnosisData The raw diagnosis response data
 * @returns Formatted data for appointments chart
 */
export const transformAppointmentsData = (diagnosisData: DiagnosisResponse): ChartData => {
  if (!diagnosisData?.stats) {
    return {
      series: [],
      categories: []
    };
  }

  // Sort diagnoses by name
  const sortedDiagnoses = _.orderBy(
    Object.entries(diagnosisData.stats),
    ([_, stats]) => stats.name.toLowerCase(),
    ['asc']
  );

  return {
    series: [{
      name: 'Total Appointments',
      data: sortedDiagnoses.map(([_, stats]) => stats.amount)
    }],
    categories: sortedDiagnoses.map(([_, stats]) => stats.name)
  };
};

/**
 * Transforms diagnosis statistics into unique patients chart data
 * @param diagnosisData The raw diagnosis response data
 * @returns Formatted data for unique patients chart
 */
export const transformUniquePatientData = (diagnosisData: DiagnosisResponse): ChartData => {
  if (!diagnosisData?.stats) {
    return {
      series: [],
      categories: []
    };
  }

  // Sort diagnoses by name
  const sortedDiagnoses = _.orderBy(
    Object.entries(diagnosisData.stats),
    ([_, stats]) => stats.name.toLowerCase(),
    ['asc']
  );

  // Create categories from diagnosis names
  const categories = sortedDiagnoses.map(([_, stats]) => stats.name);

  // Initialize counts for female, male and total
  const femaleCounts: number[] = new Array(categories.length).fill(0);
  const maleCounts: number[] = new Array(categories.length).fill(0);
  const totalCounts: number[] = new Array(categories.length).fill(0);

  // Store patient IDs by diagnosis code for later gender lookup
  const patientIdsByDiagnosis: Record<string, number[]> = {};
  sortedDiagnoses.forEach(([code, stats]) => {
    patientIdsByDiagnosis[code] = stats.patientIds;
    // Use patientsLength as the total count since it represents unique patients
    const index = categories.indexOf(stats.name);
    if (index !== -1) {
      totalCounts[index] = stats.patientsLength;
    }
  });

  return {
    series: [
      {
        name: 'Female',
        data: femaleCounts
      },
      {
        name: 'Male',
        data: maleCounts
      },
      {
        name: 'Total',
        data: totalCounts
      }
    ],
    categories,
    patientIds: patientIdsByDiagnosis
  };
};

